import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import { setCredentials } from '../features/auth/authSlice';
import { BASE } from '../config/setting';

export default function TwoFactorSetup() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const authToken = useSelector(state => state.auth.token);

  useEffect(() => {
    setupTwoFactor();
  }, []);

  // Navigate when token is set
  useEffect(() => {
    if (authToken) {
      navigate('/', { replace: true });
    }
  }, [authToken, navigate]);

  const setupTwoFactor = async () => {
    try {
      const tempToken = localStorage.getItem('tempToken');
      if (!tempToken) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${BASE}/api/2fa/setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tempToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to setup 2FA');
      }

      const data = await response.json();
      setQrCode(data.qrCode);
      setSecret(data.secret);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(null);
    setVerifying(true);

    try {
      const tempToken = localStorage.getItem('tempToken');
      if (!tempToken) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${BASE}/api/2fa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tempToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Invalid 2FA token');
      }

      const data = await response.json();
      
      // Clear temp token and set full token
      localStorage.removeItem('tempToken');
      
      // Get user from temp token
      const user = JSON.parse(atob(tempToken.split('.')[1]));
      
      // Dispatch credentials - useEffect will handle navigation
      dispatch(setCredentials({
        user: {
          _id: user.id,
          email: user.email,
          role: user.role
        },
        token: data.token
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-8">
          {/* Header Card */}
          <div className="card border-0 shadow-sm mb-4" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}>
            <div className="card-body text-white py-4">
              <h2 className="mb-0">🔐 Setup Two-Factor Authentication</h2>
              <p className="mb-0 mt-2">Secure your account with Google Authenticator</p>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger">
              ⚠️ {error}
            </div>
          )}

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h5 className="mb-3">📱 Step 1: Install Google Authenticator</h5>
              <p className="text-muted">
                Download and install Google Authenticator on your mobile device:
              </p>
              <div className="d-flex gap-3 mb-4">
                <a 
                  href="https://apps.apple.com/app/google-authenticator/id388497605" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-outline-dark"
                >
                  🍎 iOS
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn btn-outline-success"
                >
                  🤖 Android
                </a>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <h5 className="mb-3">📷 Step 2: Scan QR Code</h5>
              <p className="text-muted mb-3">
                Open Google Authenticator and scan this QR code:
              </p>
              <div className="text-center mb-4 p-3 bg-white">
                {qrCode && (
                  <img src={qrCode} alt="QR Code" style={{ maxWidth: '250px' }} />
                )}
              </div>
              <div className="alert alert-info">
                <strong>💡 Can't scan?</strong> Enter this code manually:<br />
                <code className="fs-6">{secret}</code>
              </div>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="mb-3">✅ Step 3: Verify Setup</h5>
              <p className="text-muted mb-3">
                Enter the 6-digit code from Google Authenticator:
              </p>
              <form onSubmit={handleVerify}>
                <div className="mb-3">
                  <input
                    type="text"
                    className="form-control form-control-lg text-center"
                    placeholder="000000"
                    value={token}
                    onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    pattern="\d{6}"
                    required
                    disabled={verifying}
                    style={{ letterSpacing: '0.5em', fontSize: '2rem' }}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-lg w-100"
                  disabled={verifying || token.length !== 6}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {verifying ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Verifying...
                    </>
                  ) : (
                    <>🔐 Complete Setup</>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
